import { useId, useState } from "react";
import { useTranslation } from "react-i18next";
import { exportConfiguration, minimumPassphraseLength } from "../../api/configuration";
import { useToast } from "../../components/toast/toastContext";
import panel from "../../components/admin/AdminPanel.module.css";
import styles from "./ConfigurationContent.module.css";

/**
 * Saves a passphrase-encrypted copy of this instance's configuration. The passphrase is typed twice, and the export is refused on the
 * client until both match and are long enough. The fields are cleared after a successful export, so the passphrase does not linger on
 * the page.
 */
export function ConfigurationExportPanel(): React.JSX.Element {
  const { t } = useTranslation();
  const toast = useToast();
  const passphraseId = useId();
  const confirmId = useId();
  const hintId = useId();
  const mismatchId = useId();
  const [passphrase, setPassphrase] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const tooShort = passphrase.length > 0 && passphrase.length < minimumPassphraseLength;
  const mismatch = confirm.length > 0 && confirm !== passphrase;
  const canExport = !busy && passphrase.length >= minimumPassphraseLength && confirm === passphrase;

  const onSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!canExport) {
      return;
    }

    setBusy(true);
    const result = await exportConfiguration(passphrase);
    setBusy(false);
    if (result.kind === "ok") {
      // Created and clicked in the same handler, so the download is part of the user's action.
      const url = URL.createObjectURL(result.response.blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = result.response.fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setPassphrase("");
      setConfirm("");
      toast.show(t("configAdmin.export.success", { name: result.response.fileName }), "success");
    } else if (result.kind === "denied") {
      toast.show(t("configAdmin.denied.description"), "error");
    } else {
      toast.show(result.message, "error");
    }
  };

  return (
    <section className={`${panel.panel} ${styles.section}`} aria-labelledby={`${passphraseId}-heading`}>
      <div className={panel.header}>
        <h2 id={`${passphraseId}-heading`} className={`panel-title ${panel.title}`}>
          {t("configAdmin.export.heading")}
        </h2>
      </div>
      <p className={panel.intro}>{t("configAdmin.export.description")}</p>
      <form className={styles.panel} onSubmit={(event) => void onSubmit(event)} aria-busy={busy}>
        <div className={styles.field}>
          <label htmlFor={passphraseId}>{t("configAdmin.export.passphrase")}</label>
          <input
            id={passphraseId}
            type="password"
            autoComplete="new-password"
            value={passphrase}
            aria-describedby={`${hintId} ${tooShort ? mismatchId : ""}`.trim()}
            aria-invalid={tooShort}
            onChange={(event) => setPassphrase(event.target.value)}
          />
          <p id={hintId} className={styles.hint}>
            {t("configAdmin.export.passphraseHint")}
          </p>
          {tooShort && <p id={mismatchId} className={styles.refused}>{t("configAdmin.export.tooShort")}</p>}
        </div>
        <div className={styles.field}>
          <label htmlFor={confirmId}>{t("configAdmin.export.confirm")}</label>
          <input
            id={confirmId}
            type="password"
            autoComplete="new-password"
            value={confirm}
            aria-describedby={mismatch ? mismatchId : undefined}
            aria-invalid={mismatch}
            onChange={(event) => setConfirm(event.target.value)}
          />
          {mismatch && <p id={mismatchId} className={styles.refused}>{t("configAdmin.export.mismatch")}</p>}
        </div>
        <div className={styles.actions}>
          <button type="submit" className="button button-primary" disabled={!canExport}>
            {busy && <span className={styles.spinner} aria-hidden="true" />}
            {t("configAdmin.export.button")}
          </button>
          <p className={styles.hint} role="status" aria-live="polite">{busy ? t("configAdmin.export.busy") : ""}</p>
        </div>
      </form>
    </section>
  );
}
